import { Checkbox, DatePicker, Form, Spin, Table, Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  numberWithCommas,
  parseTimestamp,
  normalizeDateToLocal,
  disabledDate,
} from "../../utils";
import TableLoading from "../../components/TableLoading";
import { fetchData } from "../../features/fetch/fetchSlice";
import BreadCrumb from "../../layout/BreadCrumb";
import Search from "../../components/Search";
import MaterialIcon from "material-icons-react";
import { downloadExcel } from "../../features/global/globalSlice";
import toast from "react-hot-toast";

const { RangePicker } = DatePicker;

export default function PaidIn() {
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const url = "api/v1/c2b/paid-in";
  const { data, count, loading } = useSelector((state) => ({
    data: state.fetch.data[url] || [],
    count: state.fetch.count[url] || 0,
    loading: state.fetch.loading[url],
  }));
  const { glbLoading } = useSelector((state) => state.global);

  const [searchInput, setsearchInput] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const onValuesChange = (changedValues) => {
    if (changedValues.hasOwnProperty("day")) {
      if (changedValues.day) {
        const startOfDay = dayjs().startOf("day");
        const endOfDay = dayjs().endOf("day");

        setStartDate(startOfDay.toDate());
        setEndDate(endOfDay.toDate());
        form.setFieldsValue({ range: [startOfDay, endOfDay] });

        dispatch(
          fetchData({
            url: url,
            kycName: searchInput,
            startDate: normalizeDateToLocal(startOfDay.toDate()),
            endDate: normalizeDateToLocal(endOfDay.toDate()),
          }),
        );
      } else {
        setStartDate(null);
        setEndDate(null);
        form.resetFields(["range"]);
        dispatch(
          fetchData({
            url: url,
            kycName: searchInput,
            startDate: null,
            endDate: null,
          }),
        );
      }
    }

    if (changedValues.hasOwnProperty("range") && changedValues.range) {
      const [start, end] = changedValues.range;
      setStartDate(start.toDate());
      setEndDate(end.toDate());

      form.setFieldsValue({ day: false });

      dispatch(
        fetchData({
          url: url,
          kycName: searchInput,
          startDate: normalizeDateToLocal(start.toDate()),
          endDate: normalizeDateToLocal(end.toDate()),
        }),
      );
    }
  };

  const handleSearch = (e) => {
    setsearchInput(e.target.value);
    dispatch(
      fetchData({
        url: url,
        kycName: e.target.value == "" ? null : e.target.value,
        start: 0,
        limit: null,
        startDate: normalizeDateToLocal(startDate),
        endDate: normalizeDateToLocal(endDate),
      }),
    );
  };

  const columns = [
    
    {
      title: "Trans Id",
      dataIndex: "transId",
      sorter: (a, b) => a.transId - b.transId,
    },
    {
      title: "Date",
      dataIndex: "transDateTime",
      render: (item) => {
        return <div>{parseTimestamp(item)}</div>;
      },
      sorter: (a, b) => a.transDateTime.localeCompare(b.transDateTime),
    },

    {
      title: "Organization",
      dataIndex: "orgName",
      sorter: (a, b) => a.orgName - b.orgName,
    },
    {
      title: "Type",
      dataIndex: "transType",
      sorter: (a, b) => a.transType - b.transType,
    },
    {
      title: "Commission",
      dataIndex: "commission",
      render: (item) => {
        return <div>{numberWithCommas(item)}</div>;
      },
      sorter: (a, b) => a.commission - b.commission,
    },
    {
      title: "Amount",
      dataIndex: "transAmount",
      render: (item) => {
        return <div>{numberWithCommas(item)}</div>;
      },
      sorter: (a, b) => a.transAmount - b.transAmount,
    },

    {
      title: "Name",
      dataIndex: "kycName",
      sorter: (a, b) => a.kycName - b.kycName,
    },

    {
      title: "Reference Number",
      dataIndex: "billRefNumber",
      sorter: (a, b) => a.billRefNumber - b.billRefNumber,
    },
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      sorter: (a, b) => a.invoiceNumber - b.invoiceNumber,
    },
    {
      title: "Short Code",
      dataIndex: "businessShortCode",
      sorter: (a, b) => a.businessShortCode - b.businessShortCode,
    },
    {
      title: "Status",
      dataIndex: "transStatus",
      render: (item) => {
        return (
          <div className="text-[#FFF] bg-[#2DD683] inline-block rounded-[5px] px-2 py-1 w-auto">
            {item}
          </div>
        );
      },
    },
  ];

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  async function fetchTableData(page, size) {
    await dispatch(
      fetchData({
        url: url,
        limit: size ?? pageSize,
        start: page ?? pageIndex,
        kycName: searchInput,
        startDate: normalizeDateToLocal(startDate),
        endDate: normalizeDateToLocal(endDate),
      }),
    );
  }

  useEffect(() => {
    fetchTableData();
  }, []);

  const breadList = [
    {
      title: (
        <div className="flex items-center gap-[.25rem]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
          >
            <g clipPath="url(#clip0_627_13942)">
              <path
                d="M13.7899 6.89067L7.75241 0.856289L7.34772 0.451602C7.25551 0.360003 7.13082 0.308594 7.00085 0.308594C6.87087 0.308594 6.74618 0.360003 6.65397 0.451602L0.211783 6.89067C0.117301 6.98478 0.0426296 7.09687 -0.00782254 7.22031C-0.0582747 7.34376 -0.0834854 7.47607 -0.0819665 7.60942C-0.0757165 8.15942 0.382096 8.59848 0.932096 8.59848H1.59616V13.6875H12.4055V8.59848H13.0837C13.3508 8.59848 13.6024 8.49379 13.7915 8.30473C13.8846 8.21193 13.9583 8.10159 14.0085 7.98009C14.0586 7.8586 14.0842 7.72836 14.0837 7.59692C14.0837 7.33129 13.979 7.07973 13.7899 6.89067ZM7.87585 12.5625H6.12585V9.37504H7.87585V12.5625ZM11.2805 7.47348V12.5625H8.87585V9.00004C8.87585 8.65473 8.59616 8.37504 8.25085 8.37504H5.75085C5.40553 8.37504 5.12585 8.65473 5.12585 9.00004V12.5625H2.72116V7.47348H1.22116L7.00241 1.69691L7.36335 2.05785L12.7821 7.47348H11.2805Z"
                fill="black"
                fillOpacity="0.45"
              />
            </g>
            <defs>
              <clipPath id="clip0_627_13942">
                <rect width="14" height="14" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <span>Home</span>
        </div>
      ),
      href: "/#/home",
    },
    {
      title: <div className="text-lighGold text-[16px]">PaidIn Summary</div>,
    },
  ];

  const handleDownload = async (item) => {
    const res = await dispatch(
      downloadExcel({
        url: "api/v1/c2b/downloadTransactions",
        kycName: searchInput,
        startDate: normalizeDateToLocal(startDate),
        endDate: normalizeDateToLocal(endDate),
      }),
    );
    if (res?.payload) {
      const blobData = res.payload.data;
      const headers = res.payload.headers;
      const contentType = headers["content-type"] || "application/octet-stream";
      const contentDisposition = headers["content-disposition"];
      let fileName = "paidIn_paidOut";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }
      const blob = new Blob([blobData], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      await toast.success(`${fileName} downloaded successfully`);
    } else {
      toast.error("Failed to download file");
    }
  };

  if (loading) return <TableLoading />;
  return (
    <>
      <div className="w-full h-full overflow-y-scroll">
        <div className="justify-between items-center white_card">
          <BreadCrumb
            breadList={breadList}
            header={"PaidIn Summary"}
            info={"Here’s an overview of your paidIn"}
          />
        </div>
        <div className="bg-white rounded-[10px] mt-[1.25rem] h-full">
          <div className="flex flex-col w-full lg:px-10 px-3 ">
            <div className="flex lg:flex-row flex-col w-full justify-between">
              <h3 className="heading_4 mt-[2rem]">PaidIn</h3>
              <div className="justify-end flex">
                <Form
                  layout="vertical"
                  ref={formRef}
                  name="control-ref"
                  onValuesChange={onValuesChange}
                  className=""
                  style={{
                    maxWidth: "100%",
                  }}
                  form={form}
                >
                  <div className="flex items-center lg:flex-row flex-col gap-5">
                    <Form.Item name="day" valuePropName="checked">
                      <Checkbox>Today</Checkbox>
                    </Form.Item>

                    <Form.Item name="range">
                      <RangePicker disabledDate={disabledDate} />
                    </Form.Item>
                  </div>
                </Form>
              </div>
            </div>
            <div className="flex lg:flex-row flex-col gap-3 justify-between item-center mt-[2rem]">
              <div className="w-[16.8125rem]">
                <Search
                  searchInput={searchInput}
                  handleSearch={handleSearch}
                  text={"Search name..."}
                />
              </div>

              {data?.length > 0 && (
                <div className="flex justify-end item-center">
                  {glbLoading ? (
                    <Spin className="sms-spin" />
                  ) : (
                    <Tooltip placement="top" title={"Download Excel"}>
                      <button
                        onClick={handleDownload}
                        className="flex items-center"
                      >
                        <MaterialIcon
                          size={45}
                          color="#00B050"
                          icon="article"
                        />
                        <span>Export to excel</span>
                      </button>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
            <div className="mb-5">
              <section>
                <Table
                  rowSelection={false}
                  className="mt-[1.31rem] w-full"
                  scroll={{
                    x: "auto",
                  }}
                  rowKey={(record) => record?.id}
                  columns={columns}
                  dataSource={data}
                  loading={loading}
                  rowClassName={(record) =>
                    record?.transStatus != "RECEIPTED" ? "row-error" : ""
                  }
                  pagination={{
                    position: ["bottomCenter"],
                    current: pageIndex + 1,
                    total: count,
                    pageSize: pageSize,
                    onChange: (page, size) => {
                      setPageIndex(page - 1);
                      setPageSize(size);
                      fetchTableData(page - 1, size);
                    },
                    showSizeChanger: false,
                    hideOnSinglePage: true,
                  }}
                />
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
